import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { desc } from "drizzle-orm";
import { Calendar, MapPin, Plus, Settings } from "lucide-react";
import { getDb } from "@/db";
import { events } from "@/db/schema";

const getEvents = createServerFn({ method: "GET" }).handler(async () => {
	return getDb().select().from(events).orderBy(desc(events.date));
});

export const Route = createFileRoute("/admin/events/")({
	loader: () => getEvents(),
	component: AdminEventsPage,
});

function statusBadgeClass(status: string) {
	switch (status) {
		case "setup":
			return "bg-status-setup text-foreground";
		case "qualifying":
			return "bg-status-qualifying text-foreground";
		case "finals":
			return "bg-status-finals text-foreground";
		default:
			return "bg-status-complete text-foreground";
	}
}

function AdminEventsPage() {
	const eventList = Route.useLoaderData();

	return (
		<div className="min-h-screen bg-background p-6">
			<div className="max-w-4xl mx-auto">
				<div className="flex items-center justify-between mb-8">
					<h1 className="text-4xl text-foreground">Manage Events</h1>
					<Link
						to="/admin/events/new"
						className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-accent text-primary-foreground font-bold rounded-lg transition-colors uppercase text-sm"
					>
						<Plus size={18} />
						New Event
					</Link>
				</div>

				{eventList.length === 0 ? (
					<div className="bg-card rounded-xl p-12 text-center">
						<p className="text-foreground text-lg mb-4">No events yet</p>
						<Link
							to="/admin/events/new"
							className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-accent text-primary-foreground font-bold rounded-lg transition-colors uppercase"
						>
							<Plus size={18} />
							Create First Event
						</Link>
					</div>
				) : (
					<div className="space-y-3">
						{eventList.map((event) => (
							<div
								key={event.id}
								className="flex items-center justify-between bg-card rounded-xl p-5"
							>
								<div>
									<h2 className="text-lg font-bold text-foreground">
										{event.name}
									</h2>
									<div className="flex flex-wrap gap-3 text-sm text-muted-foreground mt-1">
										<span className="flex items-center gap-1">
											<Calendar size={14} />
											{event.date}
										</span>
										{event.location && (
											<span className="flex items-center gap-1">
												<MapPin size={14} />
												{event.location}
											</span>
										)}
										<span
											className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${statusBadgeClass(event.status)}`}
										>
											{event.status}
										</span>
									</div>
								</div>
								<div className="flex gap-2">
									<Link
										to="/admin/events/$eventId"
										params={{ eventId: String(event.id) }}
										className="inline-flex items-center gap-1 px-3 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm"
									>
										<Settings size={14} />
										Manage
									</Link>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
}
